import isObject from 'lodash-es/isObject';
import mergeWith from 'lodash-es/mergeWith';
import { storeToRefs } from 'pinia';

import { SPLIT_NAMES, SPLIT_TREATMENTS } from '@/constants/featureFlags';
import { LanguagePayload } from '@/models/translation';
import { PartialUserSettings, UserSettings, UserSettingsByPage } from '@/models/user';
import { useCompanyStore } from '@/piniaStores/company/company.store';
import api from '@/services/api';
import { UpdateUserParams } from '@/services/api/user';
import { isHttpError } from '@/utils/errors';
import hashCode from '@/utils/hashCode';

import { UserState } from './user.state';

export const setupUserActions = (state: UserState) => {
  const { activeCompany } = storeToRefs(useCompanyStore());

  const fetchSplitExperiments = async (payload: string) => {
    return await api.user.getSplits(payload);
  };

  const fetchSplits = async (payload: string) => {
    const splits = await api.user.getSplits(payload);

    state.user.value.splits = splits;

    return splits;
  };

  const fetchUser = async () => {
    const user = await api.user.getUser();

    Object.assign(state.user.value, user);

    return user;
  };

  const fetchS2SPermissions = async () => {
    const staffId = state.userActiveCompany.value?.staffId;
    const seedToSaleVendor = activeCompany.value?.seedToSaleVendor;

    if (!staffId || !seedToSaleVendor) {
      return;
    }

    try {
      const permissions = await api.traceability.getActiveCompanyS2SStaffPermission(staffId);

      delete permissions.companyId;
      delete permissions.externalId;
      delete permissions.id;

      state.user.value.permissions.seedToSale = { ...state.user.value.permissions.seedToSale, ...permissions };
    } catch (error) {
      if (isHttpError(error) && error.response?.status === 402) {
        return; // the delinquencyGuard in router/guards.ts will redirect the user to update their billing info
      } else {
        throw error;
      }
    }
  };

  interface RemoveUserSettingsByPagePayload<K extends keyof UserSettings> {
    name: K;
    options?: {
      global: boolean;
    };
  }

  /**
   * Deletes a user setting for a specific page and relies on piniaLocalStoragePlugin to remove that setting from localStorage.
   */
  const removeUserSettingsByPage = <K extends keyof UserSettings>({
    name,
    options = {
      /** @deprecated - use `removePersistentItem` from Stash with the `global` option */
      global: false,
    },
  }: RemoveUserSettingsByPagePayload<K>) => {
    if (state.user.value.settings?.[name] === undefined) {
      return;
    }

    if (options?.global) {
      return delete state.user.value.settings[name];
    }

    // remove trailing slash from the path to delete the correct setting
    const path = location.pathname.replace(/\/$/, '');
    const key = hashCode(path);
    if (state.user.value.settings[name]?.[key] === undefined) {
      return;
    }

    return delete state.user.value.settings[name][key];
  };

  interface RemoveUserSettingsByUserPayload<K extends keyof UserSettings> {
    /**
     * User for whom the settings will be removed (optional).
     * By default, it uses the user from the state.
     */
    userId?: number;
    name: K;
  }

  /**
   * Deletes a user setting for a specific user and relies on piniaLocalStoragePlugin to remove that setting from localStorage.
   */
  const removeUserSettingsByUser = <K extends keyof UserSettings>({
    userId,
    name,
  }: RemoveUserSettingsByUserPayload<K>) => {
    const user = userId || state.user.value.id;
    if (!user) {
      throw new Error('A valid user id is required to remove user settings');
    }

    if (!state.user.value.settingsByUser[user]) {
      return;
    }

    const key = hashCode(user.toString());
    if (state.user.value.settingsByUser[key][name] === undefined) {
      return;
    }

    return delete state.user.value.settingsByUser[key][name];
  };

  const setAlgoliaQueryId = (payload: string) => {
    state.user.value.algoliaQueryId = payload;
  };

  interface SetUserSettingsByPagePayload<K extends keyof UserSettings> {
    name: K;
    data: PartialUserSettings<K>;
    options?: {
      /** @deprecated - use `setPersistentItem` from Stash with the `global` option */
      global: boolean;
    };
  }

  /**
   * Set user settings for a specific location pathname and relies on piniaLocalStoragePlugin to save the settings in localStorage.
   */
  const setUserSettingsByPage = <K extends keyof UserSettings>({
    name,
    data,
    options,
  }: SetUserSettingsByPagePayload<K>) => {
    if (!name) {
      throw new Error(`A valid name is required to set user settings`);
    }

    let updatedSetting: Partial<UserSettingsByPage> = {};

    if (options?.global) {
      updatedSetting = { [name]: data };
    } else {
      // remove trailing slash from the path to avoid duplicate settings
      const path = location.pathname.replace(/\/$/, '');
      const key = hashCode(path);

      updatedSetting = { [name]: { [key]: data } };
    }

    state.user.value.settings = updateSettings(state.user.value.settings, updatedSetting);
  };

  interface SetUserSettingsByUserPayload<K extends keyof UserSettings> {
    /**
     * User for whom the settings will be applied (optional).
     * By default, it uses the user from the state.
     */
    userId?: number;
    name: K;
    data: PartialUserSettings<K>;
  }

  /**
   * Set user settings for a specific user and relies on piniaLocalStoragePlugin to save the settings in localStorage.
   */
  const setUserSettingsByUser = <K extends keyof UserSettings>({
    userId,
    name,
    data,
  }: SetUserSettingsByUserPayload<K>) => {
    const user = userId || state.user.value.id;
    if (!user) {
      throw new Error('A valid user id is required to set user settings');
    }

    const key = hashCode(user.toString());
    const updatedSetting = { [key]: { [name]: data } };
    state.user.value.settingsByUser = updateSettings(state.user.value.settingsByUser, updatedSetting);
  };

  const updateLanguage = (payload: LanguagePayload) => {
    return api.user.updateLanguage(payload);
  };

  const updateUser = async (payload: UpdateUserParams) => {
    const updatedUser = await api.user.updateUser(payload);

    Object.assign(state.user.value, updatedUser);

    return updatedUser;
  };

  const getAuthenticationStatus = async () => {
    const isUserAuthenticated = await api.user.getAuthenticationStatus();

    state.isUserAuthenticated.value = isUserAuthenticated;

    return isUserAuthenticated;
  };

  /**
   * Clears all user settings from the page store
   */
  const clearUserSettingsByPage = () => {
    state.user.value.settings = {};
  };

  /**
   * Clears all user settings from the settings by user store
   */
  const clearUserSettingsByUser = () => {
    state.user.value.settingsByUser = {};
  };

  /**
   * Helper method to merge a new setting to the user settings object.
   * @note: To prevent unnecessary object nesting and issues with object reference mutating the state,
   * a shallow merge strategy was required.
   * `merge` and `cloneDeep` methods won't work in this case, since both methods merge deep.
   */
  function updateSettings<T extends Record<string, unknown>>(settings: T, updatedSetting: T): T {
    return mergeWith(settings, updatedSetting, (setting, newSetting) => {
      if (isObject(setting) && isObject(newSetting)) {
        return mergeWith({}, setting, newSetting, (settingValues, newSetttingValue) => {
          // Prevent deep merging beyond the second level
          return isObject(settingValues) && isObject(newSetttingValue) ? { ...newSetttingValue } : newSetttingValue;
        });
      }

      return undefined;
    });
  }

  function isProductEditMVPEnabled() {
    const isProductMVPSplitEnabled = state.getIsSplitEnabled.value(SPLIT_NAMES.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE);
    const isProductMVPTreatmentEnabled = state.getIsSplitEnabled.value(
      SPLIT_NAMES.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE,
      [SPLIT_TREATMENTS.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE_MVP],
    );
    const isProductMVPTreatmentLegacyEnabled = state.getIsSplitEnabled.value(
      SPLIT_NAMES.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE,
      [SPLIT_TREATMENTS.VUE_INVENTORY_PRODUCT_ADD_EDIT_PAGE_LEGACY],
    );

    const { isProductAddEditMVP } = state.getUserSettingsByUser.value('inventory') || {};

    return (
      // split enabled
      isProductMVPSplitEnabled ||
      // split treatment enabled or user choose to stay w/ the MVP experience
      (isProductMVPTreatmentEnabled && (isProductAddEditMVP === undefined || Boolean(isProductAddEditMVP))) ||
      // split treatment legacy enabled and user choose to stay w/ the MVP experience
      (isProductMVPTreatmentLegacyEnabled && Boolean(isProductAddEditMVP))
    );
  }

  return {
    fetchSplitExperiments,
    fetchSplits,
    fetchUser,
    fetchS2SPermissions,
    removeUserSettingsByPage,
    removeUserSettingsByUser,
    setAlgoliaQueryId,
    setUserSettingsByPage,
    setUserSettingsByUser,
    updateLanguage,
    updateUser,
    getAuthenticationStatus,
    clearUserSettingsByPage,
    clearUserSettingsByUser,
    isProductEditMVPEnabled,
  };
};
